// ©2024 Austin App House. All rights reserved.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toggleModal } from '../../storage/actions/modalsActions';
import { connect } from 'react-redux';
import { ResourceDataTable } from '../../components/datatables/ResourceDataTable';
import { recruitColumns } from '../../utils/constans';
import deleteRecruit from '../../utils/api/delete/deleteRecruit';
import { reloadData } from '../../storage/actions/applicationStateActions';
import { SocketContext } from '../../pages';
import Notification from '../../components/notification';
import DashboardIcon from '../../components/dashboard/icons/DashboardIcon';

export const Recruits = ({ toggleModal, isReload, reloadData }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tableVersion, setTableVersion] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const removing = useRef(false);
  const { notificationSocket: socket } = useContext(SocketContext);

  useEffect(() => {
    if (!socket) return undefined;

    const handleDoneImports = (response) => {
      const { message, type } = response;
      Notification(type, { message });
      reloadData();
    };

    socket.on('doneImports', handleDoneImports);

    return () => {
      socket.off('doneImports', handleDoneImports);
    };
  }, [socket, reloadData]);

  const handleDeleteRecruit = useCallback((row) => {
    toggleModal(true, 'confirmModal', true, {
      onAccept: async () => {
        if (removing.current) return;
        removing.current = true;
        toggleModal(true, 'loader');
        try {
          const result = await deleteRecruit(row._id);
          if (result.success) setRefreshToken(token => token + 1);
        } finally {
          removing.current = false;
          toggleModal(false, 'loader');
        }
      },
      header: `Remove ${row.name || 'recruit'}?`,
      content: 'This recruit will be removed from future RecruitBoost recipient lists. Emails already being sent cannot be recalled.',
      buttonLabels: {
        reject: 'Cancel',
        confirm: 'Remove',
      },
    });
  }, [toggleModal]);

  // Build the action column before rendering rather than relying on the table
  // hook's effect to mutate columns after the first render.
  const columns = useMemo(() => [...recruitColumns, {
    name: 'Actions',
    button: true,
    width: '150px',
    cell: row => (
      <button
        type="button"
        className="subpage-btn"
        aria-label={`Remove ${row.name || 'recruit'}`}
        onClick={() => handleDeleteRecruit(row)}
      >
        <DashboardIcon name="trashCan" size={16} />
        Remove
      </button>
    ),
  }], [handleDeleteRecruit]);

  return (
    <div className="subpage-wrap subpage-wrap--admin">
      <div className="subpage-header">
        <h1 className="subpage-title">Recruits</h1>
        <button
          type="button"
          className="subpage-btn subpage-btn--gold"
          aria-label="Upload CSV"
          onClick={() => toggleModal(true, 'importRecruitsModal')}
        >
          <DashboardIcon name="plus" size={18} strokeWidth="2.5" />
          Upload CSV
        </button>
      </div>

      <form className="subpage-filters" onSubmit={(event) => {
        event.preventDefault();
        setSearch(searchInput.trim());
        setTableVersion(version => version + 1);
      }}>
        <input
          type="search"
          name="search"
          className="dashboard-input subpage-filters__search"
          aria-label="Search recruits by name or email"
          placeholder="Search name or email"
          maxLength={200}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <button type="submit" className="subpage-btn subpage-btn--gold">Search</button>
        <button type="button" className="subpage-btn" onClick={() => {
          setSearchInput('');
          setSearch('');
          setTableVersion(version => version + 1);
        }}>Clear</button>
      </form>

      <div className="subpage-table-scroll">
        <ResourceDataTable
          key={tableVersion}
          entity={search ? `/api/recruits?search=${encodeURIComponent(search)}` : '/api/recruits'}
          columns={columns}
          isReload={isReload}
          refreshToken={refreshToken}
          sortBy="name"
          className="subpage-datatable"
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
  reloadData,
};

const mapStateToProps = state => ({
  isReload: state.applicationState.isReload,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Recruits);
